import { Button, Col, DatePicker, Divider, Edit, Form, getValueFromEvent, Icons, Input, message, Row, SaveButton, Select, Typography, Upload, useFileUploadState, useForm, useSelect } from "@pankod/refine-antd";
import { IResourceComponentsProps, useApiUrl, useOne } from "@pankod/refine-core";
import { ShowUserCard } from "components/profile/showUserCard";
import { CancelButton } from "components/utils/cancelButton";
import { LookupButton } from "components/utils/lookupButton";
import dayjs from "dayjs";
import { ICloudOrganization, ICloudOrganizationGroup, ICloudScreening, ICountry, IScreeningMetadata } from "interfaces";
import { MediaType } from "interfaces/enum";
import { useState } from "react";
import { DATAPROVIDER_LOOKUP, DATAPROVIDER_UPDATE, DATAPROVIDER_UPLOAD, RESOURCE_PATH, STALE_DURATION } from "scripts/site";

export const CloudScreeningEdit: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps, queryResult, form, formLoading } = useForm<ICloudScreening>({
    dataProviderName: DATAPROVIDER_UPDATE,
    redirect: "show",

  });

  const { data: cloudScreeningMetadata } = useOne<IScreeningMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.CLOUD_SCREENING,
    queryOptions: {
      enabled: true,
      staleTime: STALE_DURATION,
    },
  });

  const record = queryResult?.data?.data;

  const supplierIds = cloudScreeningMetadata?.data.config?.supplierId || [];

  const { selectProps: cloudOrganizationProps } = useSelect<ICloudOrganization>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.CLOUD_ORGANIZATION,
    optionLabel: "name",
    optionValue: "id",
    fetchSize: 100,
    filters: [
      {
        field: "enabled",
        operator: "eq",
        value: true,
      },
    ],
    sort: [
      {
        field: "name",
        order: "asc"
      },
    ],
  });


  const [cloudOrganizationId, setCloudOrganizationId] = useState<string | undefined>(undefined);

  // Ensure cloudOrganization is set from record before rendering
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  if (isInitialLoad && !formLoading && record) {
    setIsInitialLoad(false);
    setCloudOrganizationId(record.cloudOrganizationId);
  }

  const { selectProps: cloudOrganizationGroupProps, queryResult: cloudOrganizationGroupQueryResult } = useSelect<ICloudOrganizationGroup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.CLOUD_ORGANIZATION_GROUP,
    optionLabel: "name",
    optionValue: "id",
    fetchSize: 100,
    sort: [
      {
        field: "name",
        order: "asc"
      },
    ],
    filters: [
      {
        field: "cloudOrganizationId",
        operator: "eq",
        value: cloudOrganizationId,
      },
      {
        field: "enabled",
        operator: "eq",
        value: true,
      },
    ],
    queryOptions: {
      enabled: (cloudOrganizationId !== undefined && cloudOrganizationId !== ""), // Only fetch if cloudOrganization is selected
    },
  });

  const apiUrl = useApiUrl(DATAPROVIDER_UPLOAD);
  const todaysDate = dayjs().add(1, "day"); // Add one day to today's date to prevent past dates

  if (queryResult?.data?.data?.screeningDate) {
    queryResult.data.data.screeningDate = dayjs(queryResult?.data?.data?.screeningDate);
  }

  const twoColumnLayout = {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 16 },
    lg: { span: 10 },
    xl: { span: 10 },
    xxl: { span: 16 },
  }; // Responsive layout for two columns

  const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 }; // Gutter for row spacing

  const { data: physicalLocation } = useOne<ICountry>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.COUNTRY,
    id: record?.physicalLocationId ?? "",
    queryOptions: {
      enabled: !!record?.physicalLocationId,
    },
  });
  const { onChange: onUploadChange, isLoading: isUploading } = useFileUploadState();


  return (
    <Edit
      isLoading={queryResult?.isFetching}
      title={record?.name}
      headerButtons={() => (
        <>
        </>
      )}
      footerButtons={() => (
        <>
          <SaveButton {...saveButtonProps} loading={isUploading} />
          <CancelButton />
        </>
      )}
    >
      <Form
        {...formProps}
        autoComplete="off"
        layout="vertical">
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5}>TCS Employee Id</Typography.Title>
            <Typography.Text copyable>
              {record?.tcsEmployeeId}
            </Typography.Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5}>Associate</Typography.Title>
            <Typography.Text>
              <ShowUserCard id={record?.associateId} />
            </Typography.Text>
          </Col>
        </Row>

        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5}>Manager</Typography.Title>
            <Typography.Text>
              <ShowUserCard id={record?.managerUserId} />
            </Typography.Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Supplier Id"
              name="supplierId"
              tooltip="Please check the PO where current Microsoft alias is tagged in Employee Central (aka Headtrax)"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                placeholder="Select Supplier Id"
                allowClear
                options={supplierIds?.map((supplier) => ({
                  label: supplier,
                  value: supplier,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Cloud Organization"
              name="cloudOrganizationId"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                {...cloudOrganizationProps}
                allowClear
                placeholder="Select Cloud Organization"
                showSearch
                onChange={function (val, option) {
                  setCloudOrganizationId(val?.toString());
                  form?.setFieldsValue({
                    cloudOrganizationGroupId: "", // Clear cloudOrganizationGroupId
                    // cloudOrganizationName: (option && !Array.isArray(option) ? option?.label : undefined)
                  });
                }}
              />
            </Form.Item>
          </Col>

          <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              label="Cloud Group"
              name="cloudOrganizationGroupId"
              rules={[
                {
                  required: true,
                  type: "string",
                  whitespace: true,
                },
              ]}
            >
              <Select
                {...cloudOrganizationGroupProps}
                allowClear
                placeholder="Select Cloud Organization Group"
                showSearch
                disabled={(!cloudOrganizationId) || cloudOrganizationGroupQueryResult?.isFetching}
                loading={cloudOrganizationGroupProps?.loading || cloudOrganizationGroupQueryResult?.isFetching} // loading state
              // onChange={function (val, option) {
              //   form.setFieldsValue({ cloudOrganizationGroupName: (option && !Array.isArray(option) ? option?.label : undefined) });
              // }}
              />
            </Form.Item>
          </Col>

          {/* <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              hidden
              label="Cloud Organization Name"
              name="cloudOrganizationName"
            >
              <Input />
            </Form.Item>
          </Col> */}

          {/* <Col className="gutter-row" {...twoColumnLayout}>
            <Form.Item
              hidden
              label="Cloud Organization Group Name"
              name="cloudOrganizationGroupName"
            >
              <Input />
            </Form.Item>
          </Col> */}
        </Row>
        <Divider orientation="left">Location</Divider>
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5}>Physical Location</Typography.Title>
            <LookupButton id={record?.physicalLocationId ?? ""} resource={RESOURCE_PATH.COUNTRY} noClick></LookupButton>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Typography.Title level={5}>Employee Central Location (aka Headtrax)</Typography.Title>
            <LookupButton id={record?.employeeCentralLocationId ?? ""} resource={RESOURCE_PATH.COUNTRY} noClick></LookupButton>

          </Col>
        </Row>

        {physicalLocation?.data?.cloudScreening?.isScreeningDataRequired === true
          && (
            <>
              <Row gutter={rowGutter} style={{ marginTop: 15 }}>
                <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    label="Screening Id"
                    name="screeningId"
                    tooltip={"HireRight screening Id"}

                    rules={[
                      {
                        required: true,
                        type: "string",
                      },
                      {
                        validator: (_, value) => {
                          const hireRightRegex = /^\w{2}-\d{6}-[A-Z0-9]{5}$/; // Regex for HireRight ID format without enforcing starting characters
                          if (!value || hireRightRegex.test(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject("Invalid HireRight Screening Id. Format: <2-character code>-<6-digit number>-<5-character alphanumeric code>");
                        },
                      },
                    ]}
                  >
                    <Input placeholder={"AA-000000-AAAAA"} />
                  </Form.Item>
                </Col>

                <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    label="Screening Date"
                    name="screeningDate"
                    tooltip="HireRight Screening completion date"
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        type: "date"
                      },
                    ]}
                  >
                    <DatePicker
                      picker="date"
                      style={{ width: 280 }}
                      disabledDate={(selDate) => selDate && (selDate > todaysDate || selDate < dayjs().subtract(22, 'months'))}
                    // onChange={function (date) {
                    //   form.setFieldsValue({
                    //     readableScreeningDate: dayjs(date).format('DD-MMM-YYYY'),
                    //   });
                    // }}
                    />
                  </Form.Item>
                </Col>

                {/* <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    hidden
                    name="readableScreeningDate"
                  >
                    <Input />
                  </Form.Item>
                </Col> */}

                <Col className="gutter-row" {...twoColumnLayout}>
                  <Form.Item
                    hidden
                    label="Screening Type"
                    name="screeningType"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        <Row gutter={rowGutter} style={{ marginTop: 20 }}>
          <Col className="gutter-row">
            <Form.Item
              label="Screenshot of Employee Central Location which highlights the Physical Location"
              name="screenshot"
              valuePropName="fileList"
              getValueFromEvent={getValueFromEvent}
              rules={[
                {
                  required: true,
                  message: "Please upload screenshot of Employee Central Location!",
                },
              ]}
            >
              <Upload
                name="file"
                accept="image/*"
                //action={`${apiUrl}/${RESOURCE_PATH.CLOUD_SCREENING}/${MediaType.Document}`}
                action={`${apiUrl}/${RESOURCE_PATH.CLOUD_SCREENING}/${record?.id ?? MediaType.Document}`}
                listType="text"
                onChange={onUploadChange}
                maxCount={1}
                beforeUpload={(file: File) => {
                  const isImageFile = file.type.search("image") === 0;
                  if (!isImageFile) {
                    message.error(`${file.name} is not an image file!`);
                  }
                  const isLt2M = file.size / 1024 / 1024 < 5;
                  if (!isLt2M) {
                    message.error(`${file.name} is greater than 5MB!`);
                  }
                  return (isImageFile && isLt2M) ? true : Upload.LIST_IGNORE; // Prevent upload if not image or too large
                }}
              >
                <Button icon={<Icons.UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
